import React from 'react';
import { Box, Stack, Typography, Grid2, FormHelperText, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    appType,
    ButtonComponent,
    CognitoCallbacks,
    CognitoErrorCodes,
    FORGOT_PASSWORD,
    TextFieldComponent,
} from '@watter/shared-components';
import { resetPassword } from 'aws-amplify/auth/cognito';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Formik, Form, ErrorMessage } from 'formik';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { openToast } from '../../store/reducers/toastSlice';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { ForgotPasswordIcon } from '../../components/icons/forgot-password-icon';
import { ForgotPasswordValidationSchema } from './forgot-password-validation-schema';
import { LoginUi } from '../login/login-style';
import { client } from '../../service/apolloClient';
import { INavigationConfig, NavigationConfig } from '../../utils/navigation-constants';

export const ForgotPassword: React.FC = () => {
    const navConfig: INavigationConfig = NavigationConfig;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const handleSubmit = async (values: { email: string }) => {
        const showToast = (message: string, severity: 'success' | 'error') => {
            dispatch(
                openToast({
                    show: true,
                    message,
                    severity,
                    isAlertOpen: true,
                }),
            );
        };

        const handleNotAuthorized = async (err: any) => {
            if (err?.message === t('forgot_password_error_message')) {
                try {
                    dispatch(showLoader());
                    const forgotPasswordResponse = await client.mutate({
                        mutation: FORGOT_PASSWORD,
                        variables: { input: { email: values.email, application_type: 3 } },
                        context: {
                            fetchAWSToken: false,
                        },
                    });
                    dispatch(hideLoader());
                    if (forgotPasswordResponse?.data?.resetPassword?.success) {
                        showToast(forgotPasswordResponse?.data?.resetPassword?.message, 'success');
                        navigate(NavigationConfig.Login);
                    }
                } catch (error: any) {
                    dispatch(hideLoader());
                    console.error('err in reset password API call', error);
                }
            } else {
                showToast(err?.message, 'error');
            }
        };

        try {
            dispatch(showLoader());
            const output: any = await resetPassword({
                username: values.email,
                options: {
                    clientMetadata: {
                        application_type: `${appType.Admin}`,
                    },
                },
            });
            const { nextStep } = output;
            dispatch(hideLoader());
            if (nextStep?.resetPasswordStep === CognitoCallbacks.CONFIRM_RESET_PASSWORD_WITH_CODE) {
                navigate(navConfig.PasswordSetup, {
                    state: {
                        username: values.email,
                        maskedNumber: nextStep?.codeDeliveryDetails?.destination,
                        type: CognitoCallbacks.CONFIRM_RESET_PASSWORD_WITH_CODE,
                    },
                });
            } else if (nextStep?.resetPasswordStep === CognitoCallbacks.DONE) {
                console.warn('Successfully reset password.');
                showToast(t('password_reset_success'), 'success');
            } else {
                throw new Error('Unexpected next step in reset password flow.');
            }
        } catch (err: any) {
            console.error(err, 'Error resetting password');
            dispatch(hideLoader());
            switch (err?.name) {
                case CognitoErrorCodes.NOT_AUTHORIZED_EXCEPTION:
                    await handleNotAuthorized(err);
                    break;
                case CognitoErrorCodes.RESET_PASSWORD_EXCEPTION:
                    showToast(t('reset_password_exception_message'), 'error');
                    break;
                case CognitoErrorCodes.USER_NOT_FOUND_EXCEPTION:
                    showToast(t('account_doesnt_exists_error'), 'error');
                    break;
                case CognitoErrorCodes.USER_HAS_NO_ACCESS_EXCEPTION:
                    showToast(t('user_has_no_access_error'), 'error');
                    break;
                case CognitoErrorCodes.LIMIT_EXCEEDED_EXCEPTION:
                    showToast(t('limit_exceeded_error'), 'error');
                    break;
                case CognitoErrorCodes.OTP_EXPIRED_EXCEPTION:
                    showToast(t('otp_expired_message'), 'error');
                    break;
                default:
                    showToast(err?.message || 'An unexpected error occurred.', 'error');
            }
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <LoginUi container spacing={{ sm: 0, lg: 2 }}>
            <Grid2
                className="imgContainer"
                size={{ sm: 12, lg: 8 }}
                display={{ xs: 'none', lg: 'flex' }}
                alignItems="center"
            >
                <Box />
            </Grid2>
            <Grid2 size={{ xs: 12, lg: 4 }} className="feildsLabels" minWidth={{ xs: '100%', lg: 'auto' }}>
                <IconButton className="backBtn" onClick={handleBack}>
                    <KeyboardBackspaceIcon />
                </IconButton>
                <Box height="100%">
                    <Box
                        pt={3}
                        px={5}
                        pb={2}
                        height="100%"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                    >
                        <Box textAlign="center" className="logo forgotIcon">
                            <ForgotPasswordIcon />
                        </Box>
                        <Typography variant="h1" py={2} textAlign="center" data-testid="forgot-password-title">
                            {t('forgot_password_text')}
                        </Typography>
                        <Typography variant="h6" py={2} textAlign="center">
                            {t('forgot_password_message')}
                        </Typography>
                        <Formik
                            initialValues={{ email: '' }}
                            validationSchema={ForgotPasswordValidationSchema()}
                            onSubmit={handleSubmit}
                        >
                            {({ values, setFieldValue }: any) => (
                                <Form>
                                    <Stack mb={1.25} className="cstInput">
                                        <TextFieldComponent
                                            name="email"
                                            type="email"
                                            value={values.email}
                                            placeholder="Email"
                                            onChange={(e: any) => setFieldValue('email', e.target.value.trim())}
                                        />

                                        <FormHelperText error>
                                            <ErrorMessage name="email" />
                                        </FormHelperText>
                                    </Stack>
                                    <Stack rowGap={2} className="actionItems">
                                        <Box className="cstBtnWrapper">
                                            <ButtonComponent
                                                size="medium"
                                                variant="contained"
                                                fullWidth
                                                buttonName={t('send')}
                                                type="submit"
                                                className="cstBtn"
                                                data-testid="forgot-password-submit"
                                            />
                                        </Box>
                                        <Box className="cstBtnWrapper">
                                            <ButtonComponent
                                                size="medium"
                                                variant="contained"
                                                fullWidth
                                                buttonName={t('back_to_login_btn')}
                                                className="cstBtn"
                                                onClick={() => navigate(NavigationConfig.Login)}
                                            />
                                        </Box>
                                    </Stack>
                                </Form>
                            )}
                        </Formik>
                    </Box>
                </Box>
            </Grid2>
        </LoginUi>
    );
};
